import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';

import { supabase } from '@/utils/supabase';

interface AppContextProps {
  isPaused: boolean;
  isResumed: boolean;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isResumed, setIsResumed] = useState<boolean>(false);

  const handlePause = useCallback(() => {
    supabase.auth.stopAutoRefresh();
    setIsPaused(true);
    setIsResumed(false);
  }, []);

  const handleResume = useCallback(async () => {
    supabase.auth.startAutoRefresh();
    try {
      await supabase.auth.refreshSession();
    } catch {
      // Queries retry after resume; a failed refresh should not block that.
    }
    setIsPaused(false);
    setIsResumed(true);
  }, []);

  useEffect(() => {
    let pauseHandle: PluginListenerHandle | undefined;
    let resumeHandle: PluginListenerHandle | undefined;
    let cancelled = false;

    const register = async () => {
      try {
        pauseHandle = await App.addListener('pause', handlePause);
        resumeHandle = await App.addListener('resume', handleResume);
        if (cancelled) {
          await pauseHandle.remove();
          await resumeHandle.remove();
        }
      } catch {
        handleResume();
      }
    };

    register();

    return () => {
      cancelled = true;
      pauseHandle?.remove();
      resumeHandle?.remove();
    };
  }, [handlePause, handleResume]);

  return (
    <AppContext.Provider
      value={{
        isPaused,
        isResumed
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
